import React, { useState, useEffect } from 'react';
import { User, Mail, Camera, Lock, Phone, Globe, UserPlus, UserSquare } from 'lucide-react';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import { collection, query, where, getDocs, updateDoc } from 'firebase/firestore';
import { auth, db } from '../../Config/Config'; // Adjust the import path

const Profile = () => {
  const [profile, setProfile] = useState({});
  const [passwords, setPasswords] = useState({
    CurrentPassword: '',
    NewPassword: '',
    ConfirmPassword: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accountModalOpen, setAccountModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const q = query(collection(db, 'users'), where('uid', '==', user.uid));
        const data = await getDocs(q);
        if (!data.empty) {
          const userData = data.docs[0].data();
          setProfile((prev) => ({ ...prev, ...userData }));
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setProfile((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswords((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveProfile = async () => {
    try {
      const userDocRef = query(collection(db, 'users'), where('uid', '==', auth.currentUser.uid));
      const userDoc = await getDocs(userDocRef);
      if (!userDoc.empty) {
        await updateDoc(userDoc.docs[0].ref, profile);
        setAccountModalOpen(true); // Open modal on success
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const handleSavePassword = async () => {
    if (!passwords.CurrentPassword) {
      setError('Current password is required!');
      return;
    }
  
    if (passwords.NewPassword !== passwords.ConfirmPassword) {
      setError('New passwords do not match!');
      return;
    }
  
    try {
      const user = auth.currentUser;
      if (!user) {
        setError('No user is currently signed in.');
        return;
      }
  
      const credential = EmailAuthProvider.credential(user.email, passwords.CurrentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, passwords.NewPassword);
      setPasswords({
        CurrentPassword: '',
        NewPassword: '',
        ConfirmPassword: '',
      });
      setError('');
      setPasswordModalOpen(true);
    } catch (error) {
      setError(error.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#000300] pb-20 pt-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header Section */}
        <div className="text-center mb-16">
          <p className="text-lg font-light text-white/70 mb-2">PERSONAL ACCOUNT MANAGEMENT</p>
          <h1 className="text-5xl font-medium text-white tracking-wide mb-4">
            <span className="bg-gradient-to-r from-white to-white/70 bg-clip-text text-transparent">Profile</span> Settings
          </h1>
          <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-white to-transparent mx-auto mb-6"></div>
          <p className="text-white/60 font-light max-w-2xl mx-auto text-xl">
            Manage your account details and security settings
          </p>
        </div>

        {/* Account Settings Section */}
        <div className="bg-[#000300] p-8 rounded-xl border border-white/10 shadow-2xl w-full mb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_50%,transparent_75%)] bg-[length:3px_3px] opacity-20"></div>
          <div className="relative">
            <h2 className="text-2xl font-semibold mb-6 text-white">Account Information</h2>
            
            <div className="space-y-5">
              {['username', 'firstName', 'lastName', 'email', 'phoneNumber', 'country'].map((field, index) => (
                <div className="flex items-center" key={index}>
                  {field === 'email' ? <Mail className="mr-3 h-5 w-5 text-white/70" /> : 
                  field === 'username' ? <UserPlus className="mr-3 h-5 w-5 text-white/70" /> : 
                  field === 'lastName' ? <UserSquare className="mr-3 h-5 w-5 text-white/70" /> : 
                  field === 'phoneNumber' ? <Phone className="mr-3 h-5 w-5 text-white/70" /> : 
                  field === 'country' ? <Globe className="mr-3 h-5 w-5 text-white/70" /> : 
                  <User className="mr-3 h-5 w-5 text-white/70" />}

                  <input
                    type={field === 'email' ? 'email' : 'text'}
                    name={field}
                    value={profile[field] || ''}
                    onChange={handleProfileChange}
                    className="bg-[#000300] border border-white/10 rounded-lg px-4 py-3 w-full text-white focus:ring-1 focus:ring-white focus:border-transparent placeholder-white/30"
                    placeholder={field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1')}
                  />
                </div>
              ))}
            </div>
            
            <div className="mt-8 flex justify-end space-x-4">
              <button
                className="bg-white/5 hover:bg-white/10 text-white py-3 px-6 rounded-lg border border-white/10 hover:border-white/20 transition-all duration-300 shadow-lg font-medium relative overflow-hidden"
                onClick={handleSaveProfile}
              >
                <span className="relative z-10">Save Profile</span>
                <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(255,255,255,0.2)_50%,transparent_100%)] opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>
        </div>

        {/* Password Change Section */}
        <div className="bg-[#000300] p-8 rounded-xl border border-white/10 shadow-2xl w-full relative overflow-hidden">
          <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(255,255,255,0.05)_50%,transparent_75%)] bg-[length:3px_3px] opacity-20"></div>
          <div className="relative">
            <h2 className="text-2xl font-semibold mb-6 text-white">Change Password</h2>
            
            <div className="space-y-5">
              {['CurrentPassword', 'NewPassword', 'ConfirmPassword'].map((field, index) => (
                <div className="flex items-center" key={index}>
                  <Lock className="mr-3 h-5 w-5 text-white/70" />
                  <input
                    type="password"
                    name={field}
                    value={passwords[field] || ''}
                    onChange={handlePasswordChange}
                    className="bg-[#000300] border border-white/10 rounded-lg px-4 py-3 w-full text-white focus:ring-1 focus:ring-white focus:border-transparent placeholder-white/30"
                    placeholder={field.replace(/([A-Z])/g, ' $1')}
                  />
                </div>
              ))}
              
              {error && <p className="text-red-400 text-center">{error}</p>}
            </div>
            
            <div className="mt-8 flex justify-end space-x-4">
              <button
                className="bg-white hover:bg-white/90 text-[#000300] py-3 px-6 rounded-lg transition-all duration-300 shadow-lg font-medium relative overflow-hidden"
                onClick={handleSavePassword}
              >
                <span className="relative z-10">Update Password</span>
                <span className="absolute inset-0 bg-[linear-gradient(90deg,transparent_0%,rgba(0,3,0,0.2)_50%,transparent_100%)] opacity-0 hover:opacity-100 transition-opacity duration-300"></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Account Settings Modal */}
      {accountModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-[#000300] rounded-xl max-w-md w-full border border-white/10 overflow-hidden shadow-2xl">
            <div className="h-[2px] bg-white"></div>
            <div className="p-8">
              <h3 className="text-2xl font-medium text-white mb-2">Profile Updated!</h3>
              <p className="text-white/70 mb-6">Your account settings have been updated successfully.</p>
              <button 
                onClick={() => setAccountModalOpen(false)}
                className="w-full bg-white hover:bg-white/90 text-[#000300] py-3 px-6 rounded-lg transition-all duration-300 font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Password Update Modal */}
      {passwordModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
          <div className="bg-[#000300] rounded-xl max-w-md w-full border border-white/10 overflow-hidden shadow-2xl">
            <div className="h-[2px] bg-white"></div>
            <div className="p-8">
              <h3 className="text-2xl font-medium text-white mb-2">Password Updated!</h3>
              <p className="text-white/70 mb-6">Your password has been updated successfully.</p>
              <button 
                onClick={() => setPasswordModalOpen(false)}
                className="w-full bg-white hover:bg-white/90 text-[#000300] py-3 px-6 rounded-lg transition-all duration-300 font-medium"
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;